import { useState } from 'preact/hooks'
import type { LiftSummary, WeeklyLiftFigure } from '~/calculations/estimated1RM'
import { Button } from '~/components/primitives/Button'
import { NumericField, TextField } from '~/components/primitives/Field'
import { DEFAULT_RULES_THRESHOLDS } from '~/rules/thresholds'
import type { LiftWithSummary } from '~/services/strengthService'
import { defineLift, recordSet, retireLift } from '~/services/strengthService'
import { useDatabase } from '~/storage/DatabaseContext'
import type { SentinelLiftId } from '~/types/ids'
import styles from './WeeklyReviewWizard.module.css'

const REP_CAP = DEFAULT_RULES_THRESHOLDS.strength.maxRepsCountedForEstimated1RM

interface StrengthStepProps {
  lifts: readonly LiftWithSummary[]
  onChanged: () => void
}

/**
 * Sentinel lifts (Checkpoint 2.2).
 *
 * Nothing seeds a list of lifts. The handoff's suggestions — presses, squat or
 * leg press, Romanian deadlift, a vertical pull, a row, overhead press — are
 * examples, not defaults, and creating them on the user's behalf would put lifts
 * they never chose into their history.
 *
 * The estimate shown here is derived on read, never stored, and always carries
 * the reason a set did not count.
 */
export function StrengthStep({ lifts, onChanged }: StrengthStepProps) {
  const db = useDatabase()
  const [newLift, setNewLift] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  async function addLift() {
    if (newLift.trim() === '') return
    setBusy(true)
    const created = await defineLift(db, newLift, new Date())
    setBusy(false)

    if (!created.ok) {
      setError(created.error.message)
      return
    }
    setError(undefined)
    setNewLift('')
    onChanged()
  }

  async function remove(id: SentinelLiftId) {
    setBusy(true)
    await retireLift(db, id, new Date())
    setBusy(false)
    onChanged()
  }

  return (
    <>
      <p class={styles.lede}>
        A few lifts you track every week to see whether strength is holding up. Project Sola
        estimates a one-rep max from your best set — it is an estimate from the weight and reps you
        did, never a max you have to attempt.
      </p>

      {lifts.length === 0 ? (
        <p class={styles.pending}>
          No lifts yet. Add the two or three you care most about — a press, a squat or leg press, a
          pull. You can skip this step entirely and add them later.
        </p>
      ) : null}

      {lifts.map((entry) => (
        <LiftCard key={entry.lift.id} entry={entry} onChanged={onChanged} onRetire={remove} />
      ))}

      <fieldset class={styles.scale}>
        <legend class={styles.scaleLegend}>Add a lift</legend>
        <TextField
          label="Lift name"
          hint="For example: Back squat, Bench press, Chin-up."
          value={newLift}
          onInput={setNewLift}
          {...(error === undefined ? {} : { error })}
        />
        <Button variant="secondary" onClick={() => void addLift()} disabled={busy}>
          Add this lift
        </Button>
      </fieldset>
    </>
  )
}

interface LiftCardProps {
  entry: LiftWithSummary
  onChanged: () => void
  onRetire: (id: SentinelLiftId) => void
}

function LiftCard({ entry, onChanged, onRetire }: LiftCardProps) {
  const db = useDatabase()
  const [load, setLoad] = useState('')
  const [reps, setReps] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  async function add() {
    setBusy(true)
    const added = await recordSet(
      db,
      { liftId: entry.lift.id, loadKg: Number(load), reps: Number(reps) },
      new Date(),
    )
    setBusy(false)

    if (!added.ok) {
      setError(added.error.message)
      return
    }
    setError(undefined)
    setLoad('')
    setReps('')
    onChanged()
  }

  const { summary } = entry

  return (
    <fieldset class={styles.scale}>
      <legend class={styles.scaleLegend}>{entry.lift.name}</legend>

      <p class={styles.saveState}>{describeFigure(summary.current)}</p>

      {summary.current.status === 'present' ? <Comparisons summary={summary} /> : null}

      {/* Excluded sets are named, never silently dropped (D-069). */}
      {summary.current.excludedSets.length > 0 ? (
        <p class={styles.pending}>
          {summary.current.excludedSets.length}{' '}
          {summary.current.excludedSets.length === 1 ? 'set was' : 'sets were'} not used for the
          estimate, because estimates from sets above {REP_CAP} reps are too unreliable to compare.
          They are still saved.
        </p>
      ) : null}

      <div class={styles.actions}>
        <NumericField
          label={`${entry.lift.name} weight`}
          suffix="kg"
          value={load}
          onInput={setLoad}
        />
        <NumericField label={`${entry.lift.name} reps`} value={reps} onInput={setReps} />
      </div>

      {error === undefined ? null : (
        <p class={styles.errors} role="alert">
          {error}
        </p>
      )}

      <Button
        variant="secondary"
        onClick={() => void add()}
        disabled={busy || load.trim() === '' || reps.trim() === ''}
        label={`Add a set of ${entry.lift.name}`}
      >
        Add this set
      </Button>

      {entry.setsThisWeek.length > 0 ? (
        <ul class={styles.summaryList}>
          {entry.setsThisWeek.map((set) => (
            <li key={set.id}>
              {set.loadKg} kg × {set.reps}
              {set.reps > REP_CAP ? ' — not counted towards the estimate' : ''}
            </li>
          ))}
        </ul>
      ) : null}

      <Button
        variant="secondary"
        onClick={() => onRetire(entry.lift.id)}
        label={`Stop tracking ${entry.lift.name}`}
      >
        Stop tracking this lift
      </Button>
    </fieldset>
  )
}

/**
 * The four comparisons D-069 asks for: previous week, four-week average, change
 * since start, and strength relative to body weight.
 *
 * Each is omitted when its input is absent rather than shown as a zero or a
 * dash — the same rule the weekly figure itself follows.
 */
function Comparisons({ summary }: { summary: LiftSummary }) {
  return (
    <ul class={styles.summaryList}>
      {summary.previousWeekKg === undefined ? null : (
        <li>
          Last week: {summary.previousWeekKg.toFixed(1)} kg
          {summary.changeVsPreviousWeekKg === undefined
            ? null
            : ` (${signed(summary.changeVsPreviousWeekKg)} kg)`}
        </li>
      )}
      {summary.fourWeekAverageKg === undefined ? null : (
        <li>
          Four-week average: {summary.fourWeekAverageKg.toFixed(1)} kg from{' '}
          {summary.weeksInFourWeekAverage} {summary.weeksInFourWeekAverage === 1 ? 'week' : 'weeks'}
          {summary.changeVsFourWeekPercent === undefined
            ? null
            : ` (${signed(summary.changeVsFourWeekPercent)}%)`}
        </li>
      )}
      {summary.changeVsStartKg === undefined ? null : (
        <li>Since you started tracking it: {signed(summary.changeVsStartKg)} kg</li>
      )}
      {summary.relativeToBodyWeight === undefined ? null : (
        <li>{summary.relativeToBodyWeight.toFixed(2)}× your body weight</li>
      )}
    </ul>
  )
}

function describeFigure(figure: WeeklyLiftFigure): string {
  if (figure.status === 'present') {
    return `Estimated 1RM this week: ${(figure.estimateKg as number).toFixed(1)} kg`
  }
  return figure.missingReason === 'allSetsAboveRepCap'
    ? `No estimate this week: every set was above ${REP_CAP} reps.`
    : 'No estimate this week yet. Add a set below.'
}

function signed(value: number): string {
  return `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(1)}`
}
